import { FunctionComponent, useEffect } from 'react';
import { Button } from '../../tailwindcomponents';
import { Request } from '../../types/capabilityList';
import CompareRequest from './CompareRequest';
interface PrintProps {
    setTabValue: Function;
    filteredShopsForPrint: Request[];
}

// Print-only stylesheet.
// The app shell uses `h-screen` + `overflow-hidden` on its root and
// `overflow-y-auto` on <main>. That makes <main> a scroll container,
// which means window.print() only captures what fits in the viewport
// (page 1). These rules neutralise the height/overflow constraints for
// printing, hide non-printable chrome, and add page breaks between
// capability entries so the full list prints across multiple pages.
const PRINT_STYLES = `
@media print {
    @page { size: A4 portrait; margin: 10mm; }

    html, body, #root {
        height: auto !important;
        overflow: visible !important;
        background: #fff !important;
    }

    /* Strip every background on the app shell during print.
       Selectors are deliberately specific (id + class) to outrank
       Tailwind utilities, which get extra specificity from the
       project's tailwind config (important: '#root'). */
    body.printing #root,
    body.printing #root [class*="bg-"]:not(.capability-print-entry):not(.capability-print-entry *) {
        background: transparent !important;
        background-color: transparent !important;
        box-shadow: none !important;
    }
    body.printing #root > div,
    body.printing #root > div > div,
    body.printing #main {
        height: auto !important;
        max-height: none !important;
        overflow: visible !important;
        margin-left: 0 !important;
        background: transparent !important;
        background-color: transparent !important;
        box-shadow: none !important;
    }

    /* Only force exact colors on the actual printable content so each
       component keeps its own colors. */
    .capability-print-entry,
    .capability-print-entry * {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
    }

    /* Hide app chrome and anything explicitly marked no-print */
    #header,
    aside,
    .no-print { display: none !important; }

    /* Keep each capability entry intact when possible, but allow
       multiple entries to share a page (compact print). */
    .capability-print-entry {
        page-break-inside: avoid;
        break-inside: avoid;
        margin-bottom: 6mm;
    }

    /* ---------- Compact print layout ----------
       The capability cards use generous spacing/typography for screen.
       Tighten everything inside .capability-print-entry to fit more
       entries per page. */
    .capability-print-entry {
        font-size: 10pt;
        line-height: 1.2;
    }
    .capability-print-entry .MuiPaper-root {
        box-shadow: none !important;
        padding: 0.25rem !important;
    }
    .capability-print-entry h1,
    .capability-print-entry h2 {
        font-size: 12pt !important;
        margin: 2px 0 !important;
        line-height: 1.15 !important;
    }
    .capability-print-entry h3,
    .capability-print-entry h4,
    .capability-print-entry h5,
    .capability-print-entry h6 {
        font-size: 10.5pt !important;
        margin: 1px 0 !important;
        line-height: 1.15 !important;
    }
    .capability-print-entry p,
    .capability-print-entry .MuiTypography-body1,
    .capability-print-entry .MuiTypography-body2 {
        font-size: 10pt !important;
        margin: 0 !important;
        line-height: 1.2 !important;
    }
    /* Tighten the MUI Grid spacing/margins set via the component
       (margin={[4,2,4,2]} etc.) without changing component code. */
    .capability-print-entry .MuiGrid-root,
    .capability-print-entry .MuiGrid2-root {
        margin-top: 2px !important;
        margin-bottom: 2px !important;
        padding-top: 0 !important;
        padding-bottom: 0 !important;
    }
    .capability-print-entry table {
        font-size: 9pt !important;
    }
    .capability-print-entry .MuiTableCell-root {
        padding: 2px 6px !important;
    }
    /* Hide the big separator between entries — the forced page break
       already provides the boundary. */
    .capability-print-entry hr { display: none !important; }
}
`;

const Print: FunctionComponent<PrintProps> = ({ setTabValue, filteredShopsForPrint }) => {

    useEffect(() => {
        // Toggle a `printing` class on <body> so the @media print rules
        // above have enough specificity to override Tailwind's
        // #root-scoped !important utilities (e.g. bg-gray-50 on the app
        // shell). The class is removed automatically when the print
        // dialog closes, so normal styling is restored.
        const onBefore = () => document.body.classList.add('printing');
        const onAfter = () => document.body.classList.remove('printing');
        window.addEventListener('beforeprint', onBefore);
        window.addEventListener('afterprint', onAfter);

        return () => {
            window.removeEventListener('beforeprint', onBefore);
            window.removeEventListener('afterprint', onAfter);
            document.body.classList.remove('printing');
        };
    }, [])

    const onBack = () => {
        setTabValue(0);
    }

    return (
        <div>
            <style>{PRINT_STYLES}</style>
            <div className="no-print flex gap-2 mb-2">
                <Button onClick={() => onBack()} variant="outlined">Back</Button>
                <Button onClick={() => window.print()} variant="contained">Print / Save as PDF</Button>
            </div>
            {(filteredShopsForPrint.length > 0) && filteredShopsForPrint.map((request: Request, index: number) => (
                <div key={index} className="capability-print-entry">
                    <CompareRequest
                        rIndex={index}
                        printMode={true}
                        isChanged={false}
                        request={request}
                    />
                    <hr className="my-[42px]" />
                </div>
            ))
            }
        </div >
    )
}

export default Print;